import {
  AlertTriangle,
  BriefcaseBusiness,
  Check,
  Pencil,
  Plus,
  Scale,
  Wallet,
  X,
} from "lucide-react";
import { FormEvent, useCallback, useEffect, useMemo, useState } from "react";

import api from "../api/client";
import { useAuth } from "../context/AuthContext";
import { useWorkspace } from "../context/WorkspaceContext";
import type {
  AccrualPolicy,
  LeaveRecalculation,
  LeaveType,
  OrganizationMember,
  Paginated,
  WorkOverview,
  WorkPolicyOverview,
} from "../types/api";
import { unwrapResults } from "../types/api";
import { apiErrorMessage, hasUncertainApiOutcome } from "../utils/apiError";
import { formatDate, formatMinutes } from "../utils/format";
import EmploymentSettings from "./EmploymentSettings";
import LeaveRecalculationPrompt from "./LeaveRecalculationPrompt";
import { ErrorNotice, LoadingState, SuccessNotice } from "./Feedback";
import WorkScheduleSettings from "./WorkScheduleSettings";
import DateField from "./DateField";

type PolicyDraft = {
  id: number | null;
  leave_type: number;
  frequency: AccrualPolicy["frequency"];
  unit: AccrualPolicy["unit"];
  fulltime_annual_hours: string;
  fulltime_day_hours: string;
  prorated_by_contract: boolean;
  validity_year: string;
  expires_on: string;
  start_date: string;
  active: boolean;
};

type SectionKey = "contract" | "werkpatroon" | "verlof";

export default function WorkAndLeaveSettings() {
  const { user } = useAuth();
  const { organizationId, capabilities } = useWorkspace();
  const [targetUser, setTargetUser] = useState<number | null>(user?.id ?? null);
  const [members, setMembers] = useState<OrganizationMember[]>([]);
  const [overview, setOverview] = useState<WorkOverview | null>(null);
  const [leaveTypes, setLeaveTypes] = useState<LeaveType[]>([]);
  const [draft, setDraft] = useState<PolicyDraft | null>(null);
  const [activeEmploymentId, setActiveEmploymentId] = useState<number | null>(null);
  const [addingEmployer, setAddingEmployer] = useState(false);
  const [activeSection, setActiveSection] = useState<SectionKey>("contract");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingPool, setSavingPool] = useState(false);
  const [recalculationToken, setRecalculationToken] = useState(0);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const isPersonal = !organizationId;
  const canEdit =
    !organizationId ||
    (capabilities.canManageOrganizationPolicies && !capabilities.organizationReadOnly);

  const query = useMemo(() => {
    const params = new URLSearchParams();
    if (organizationId) params.set("organization", String(organizationId));
    if (targetUser) params.set("user", String(targetUser));
    return params.toString();
  }, [organizationId, targetUser]);

  const policyQuery = useMemo(() => {
    const params = new URLSearchParams();
    if (organizationId) params.set("organization", String(organizationId));
    if (targetUser) params.set("user", String(targetUser));
    if (isPersonal && activeEmploymentId) {
      params.set("employment", String(activeEmploymentId));
    }
    return params.toString();
  }, [activeEmploymentId, isPersonal, organizationId, targetUser]);

  useEffect(() => {
    setTargetUser(user?.id ?? null);
    setMembers([]);
    setActiveEmploymentId(null);
    setAddingEmployer(false);
    setActiveSection("contract");
    setDraft(null);
  }, [organizationId, user?.id]);

  useEffect(() => {
    if (!organizationId || !capabilities.canManageOrganizationPolicies) return;
    void api
      .get<OrganizationMember[]>(`/organizations/${organizationId}/members/`)
      .then((response) =>
        setMembers(response.data.filter((member) => member.status === "active")),
      )
      .catch(() => setError("De medewerkers konden niet worden geladen."));
  }, [capabilities.canManageOrganizationPolicies, organizationId]);

  const load = useCallback(async (): Promise<WorkOverview | null> => {
    if (!targetUser) return null;
    setLoading(true);
    setError("");
    try {
      const suffix = query ? `?${query}` : "";
      const [overviewResponse, typeResponse] = await Promise.all([
        api.get<WorkOverview>(`/leave/work-overview/${suffix}`),
        api.get<Paginated<LeaveType> | LeaveType[]>("/leave/types/?scope=all"),
      ]);
      setOverview(overviewResponse.data);
      setLeaveTypes(unwrapResults(typeResponse.data));
      setActiveEmploymentId((current) => {
        const rows = overviewResponse.data.employments;
        if (current && rows.some((row) => row.id === current)) {
          return current;
        }
        return rows[0]?.id ?? null;
      });
      return overviewResponse.data;
    } catch (requestError: unknown) {
      setError(
        apiErrorMessage(
          requestError,
          "De werk- en verlofgegevens konden niet worden geladen.",
        ),
      );
      return null;
    } finally {
      setLoading(false);
    }
  }, [query, targetUser]);

  useEffect(() => {
    void load();
  }, [load]);

  const activeEmployment =
    overview?.employments.find((employment) => employment.id === activeEmploymentId) ??
    null;
  const employerManaged = isPersonal && Boolean(activeEmployment?.organization_id);
  const canEditActiveEmployment = canEdit && !employerManaged;
  const activeSchedules = isPersonal
    ? activeEmployment?.schedules ?? []
    : overview?.schedules ?? [];
  const activePolicies = isPersonal
    ? activeEmployment?.policies ?? []
    : overview?.policies ?? [];
  const currentContract = isPersonal
    ? activeEmployment?.contracts[0] ?? null
    : overview?.employments[0]?.contracts[0] ?? null;

  const newPolicy = () => {
    const taken = new Set(activePolicies.map((policy) => policy.leave_type));
    const firstFree = leaveTypes.find((leaveType) => !taken.has(leaveType.id));
    const currentYearStart = `${overview?.year ?? new Date().getFullYear()}-01-01`;
    const contractStart = currentContract?.start_date;
    const startDate = contractStart && contractStart > currentYearStart
      ? contractStart
      : currentYearStart;
    setDraft({
      id: null,
      leave_type: firstFree?.id ?? leaveTypes[0]?.id ?? 0,
      frequency: "yearly",
      unit: "hours",
      fulltime_annual_hours: "",
      fulltime_day_hours: "8",
      prorated_by_contract: false,
      validity_year: String(new Date(`${startDate}T12:00:00`).getFullYear()),
      expires_on: "",
      start_date: startDate,
      active: true,
    });
  };

  const editPolicy = (policy: WorkPolicyOverview) => {
    setDraft({
      id: policy.id,
      leave_type: policy.leave_type,
      frequency: "yearly",
      unit: "hours",
      fulltime_annual_hours: String(policy.fulltime_annual_hours || ""),
      fulltime_day_hours: policy.fulltime_day_hours,
      prorated_by_contract: policy.prorated_by_contract,
      validity_year: policy.validity_year
        ? String(policy.validity_year)
        : String(overview?.year ?? new Date().getFullYear()),
      expires_on: policy.expires_on ?? "",
      start_date: policy.start_date,
      active: true,
    });
  };

  const savePolicy = async (event: FormEvent) => {
    event.preventDefault();
    if (!draft) return;
    setSaving(true);
    setError("");
    try {
      const suffix = policyQuery ? `?${policyQuery}` : "";
      const payload = {
        ...draft,
        fulltime_annual_hours: draft.fulltime_annual_hours || "0",
        validity_year: Number(draft.validity_year),
        expires_on: draft.expires_on || null,
        start_date: draft.start_date,
      };
      if (draft.id) {
        await api.patch(`/leave/accrual-policies/${draft.id}/${suffix}`, payload);
      } else {
        await api.post(`/leave/accrual-policies/${suffix}`, payload);
      }
      setSuccess("De verlofregeling is opgeslagen. Het saldo is bijgewerkt.");
      setDraft(null);
      await load();
    } catch (requestError: unknown) {
      if (hasUncertainApiOutcome(requestError)) {
        const refreshed = await load();
        const policies = refreshed
          ? isPersonal
            ? refreshed.employments
                .flatMap((employment) => employment.policies ?? [])
            : refreshed.policies
          : [];
        const saved = policies.some(
          (policy) =>
            (draft.id === null || policy.id === draft.id) &&
            policy.leave_type === draft.leave_type &&
            Math.abs(
              policy.fulltime_annual_hours - Number(draft.fulltime_annual_hours || 0),
            ) < 0.001,
        );
        if (saved) {
          setDraft(null);
          setSuccess("De verlofregeling is opgeslagen en opnieuw ingelezen.");
          return;
        }
      }
      setError(
        apiErrorMessage(requestError, "De verlofregeling kon niet worden opgeslagen."),
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <LoadingState label="Werk & verlof laden..." />;

  const scheduleMinutes = activeSchedules[0]?.total_minutes_per_week ?? null;
  const contractMinutes = currentContract
    ? Math.round(Number(currentContract.contract_hours_per_week) * 60)
    : null;

  const saveLeavePool = async (checked: boolean) => {
    if (!activeEmploymentId) return;
    const suffix = query ? `?${query}` : "";
    // Omzetten verandert van welke pot bestaande opnames afgaan, dus eerst
    // laten zien wat er opnieuw geboekt wordt voordat het gebeurt.
    const affected = await api
      .get<LeaveRecalculation>(`/leave/recalculation/${suffix}`)
      .then((response) => response.data.years)
      .catch(() => []);
    const entryCount = affected.reduce((sum, row) => sum + row.entry_count, 0);
    if (entryCount > 0) {
      const totalMinutes = affected.reduce((sum, row) => sum + row.minutes, 0);
      const confirmed = window.confirm(
        `${checked ? "Samenvoegen" : "Splitsen"} verandert van welk saldo ` +
          "goedgekeurd verlof afgaat. " +
          `${entryCount} verlofregel(s) van in totaal ${formatMinutes(
            totalMinutes,
          )} in ${affected.map((row) => row.year).join(", ")} ` +
          "worden meteen opnieuw geboekt. Doorgaan?",
      );
      if (!confirmed) return;
    }
    setSavingPool(true);
    setError("");
    try {
      const response = await api.patch<{
        pool_recalculation?: { entries: number; years: number[] };
      }>(`/organizations/employments/${activeEmploymentId}/${suffix}`, {
        combine_atv_with_leave: checked,
      });
      const rebooked = response.data.pool_recalculation?.entries ?? 0;
      setSuccess(
        (checked
          ? "ATV en verlof tellen nu als een gezamenlijke pot."
          : "ATV en verlof hebben weer een eigen saldo.") +
          (rebooked
            ? ` ${rebooked} goedgekeurde verlofregel(s) zijn opnieuw geboekt.`
            : ""),
      );
      setRecalculationToken((value) => value + 1);
      await load();
    } catch (requestError: unknown) {
      setError(
        apiErrorMessage(requestError, "De instelling kon niet worden opgeslagen."),
      );
    } finally {
      setSavingPool(false);
    }
  };

  const chooseSection = (section: SectionKey) => {
    setActiveSection(section);
    setDraft(null);
  };

  // De contractstap draait in een genest formulier; zonder deze terugkoppeling
  // blijft het scherm op "Nieuwe werkgever" staan tot je de pagina herlaadt.
  const handleEmploymentSaved = async (employmentId: number | null) => {
    setAddingEmployer(false);
    if (employmentId) setActiveEmploymentId(employmentId);
    const refreshed = await load();
    if (employmentId && refreshed?.employments.some((row) => row.id === employmentId)) {
      setActiveEmploymentId(employmentId);
    }
    setActiveSection("werkpatroon");
  };

  const steps: { key: SectionKey; label: string; description: string }[] = [
    {
      key: "contract",
      label: "Contract",
      description: "Leg je contracturen en arbeidsfactor vast.",
    },
    {
      key: "werkpatroon",
      label: "Werkpatroon",
      description: "Verdeel je contracturen over je werkweek.",
    },
    {
      key: "verlof",
      label: "Verlofregeling",
      description: "Stel je verlofrechten en saldo in.",
    },
  ];
  const activeStepIndex = steps.findIndex((step) => step.key === activeSection);

  return (
    <div className="settings-stack">
      {error && <ErrorNotice>{error}</ErrorNotice>}
      {success && <SuccessNotice>{success}</SuccessNotice>}
      {members.length > 0 && (
        <section className="surface settings-section settings-context-bar">
          <label>
            <span>Medewerker</span>
            <select
              value={targetUser ?? ""}
              onChange={(event) => setTargetUser(Number(event.target.value))}
            >
              {members.map((member) => (
                <option key={member.user} value={member.user}>
                  {member.display_name}
                </option>
              ))}
            </select>
          </label>
        </section>
      )}

      {!isPersonal && (
        <section className="surface settings-section">
          <div className="settings-heading settings-heading-with-action">
            <div>
              <h2>Werkgever & contract</h2>
              <p>
                Fulltime norm &rarr; contract &rarr; arbeidsfactor. Deze bepalen hoeveel
                verlof je krijgt.
              </p>
            </div>
            {isPersonal && canEdit && (
              <button
                className="button primary"
                type="button"
                onClick={() => {
                  setAddingEmployer(true);
                  setActiveSection("contract");
                  setDraft(null);
                }}
              >
                <Plus size={17} /> Werkgever toevoegen
              </button>
            )}
          </div>
          {overview && overview.employments.length === 0 ? (
            <div className="empty-state compact">
              <BriefcaseBusiness size={24} />
              <h2>Nog geen werkgever</h2>
              <p>Voeg hieronder een werkgever en je contractgegevens toe.</p>
            </div>
          ) : (
            <div className="employment-list">
              {overview?.employments.map((employment) => (
                <article key={employment.id} className="employment-group">
                  <div className="employment-group-heading">
                    <span>
                      <strong>{employment.name}</strong>
                      <small>
                        Fulltime basis: {employment.contracts[0]?.fulltime_hours_per_week ??
                          employment.fulltime_hours_per_week} uur/week
                      </small>
                    </span>
                  </div>
                  {employment.contracts.length === 0 ? (
                    <p className="empty-copy">
                      Nog geen contractperiode. Voeg hieronder een contract toe.
                    </p>
                  ) : (
                    employment.contracts.map((contract) => (
                      <div key={contract.id} className="arbeidsfactor-row">
                        <div>
                          <strong>
                            {formatDate(contract.start_date)}
                            {contract.end_date
                              ? ` t/m ${formatDate(contract.end_date)}`
                              : " - doorlopend"}
                          </strong>
                          <small>
                            {contract.contract_hours_per_week} contracturen/week
                            {contract.fulltime_hours_per_week
                              ? ` op ${contract.fulltime_hours_per_week} uur fulltime`
                              : ""}
                          </small>
                        </div>
                        <span className="status-chip neutral">
                          {contract.arbeidsfactor_percentage}% van fulltime
                        </span>
                      </div>
                    ))
                  )}
                </article>
              ))}
            </div>
          )}
        </section>
      )}

      {isPersonal ? (
        <div className="work-guided-flow">
          <div
            className="work-guided-steps"
            role="tablist"
            aria-label="Werk en verlof instellen"
          >
            {steps.map((step, index) => (
              <button
                key={step.key}
                type="button"
                role="tab"
                aria-selected={activeSection === step.key}
                className={activeSection === step.key ? "active" : ""}
                onClick={() => chooseSection(step.key)}
              >
                <span className="work-guided-step-number">
                  {index < activeStepIndex ? <Check size={16} /> : index + 1}
                </span>
                <span>
                  <strong>{step.label}</strong>
                  <small>
                    {index < activeStepIndex
                      ? "Voltooid"
                      : index === activeStepIndex
                      ? "Bezig"
                      : "Volgende"}
                  </small>
                </span>
              </button>
            ))}
          </div>

          <div className="work-guided-layout">
            <div className="work-guided-main">
              {addingEmployer ? (
                <section className="surface settings-section">
                  <div className="settings-heading settings-heading-with-action">
                    <div>
                      <h2>Nieuwe werkgever</h2>
                      <p>Voeg een werkgever toe en vul de eerste contractperiode in.</p>
                    </div>
                    <button
                      className="icon-button"
                      type="button"
                      title="Sluiten"
                      onClick={() => setAddingEmployer(false)}
                    >
                      <X size={17} />
                    </button>
                  </div>
                  <EmploymentSettings
                    scopedEmploymentId={null}
                    onSaved={(employmentId) => void handleEmploymentSaved(employmentId)}
                  />
                </section>
              ) : activeEmployment ? (
                <>
                  <section className="work-guided-intro">
                    <span className="work-guided-kicker">
                      Stap {activeStepIndex + 1} van {steps.length}
                    </span>
                    <h2>{steps[activeStepIndex].label} instellen</h2>
                    <p>{steps[activeStepIndex].description}</p>
                  </section>
                  {employerManaged && (
                    <p className="read-only-note">
                      <Scale size={17} /> Deze gegevens worden door je werkgever beheerd.
                    </p>
                  )}
                  {activeSection === "contract" && !employerManaged && (
                    <EmploymentSettings
                      scopedEmploymentId={activeEmployment.id}
                      onSaved={() => void load()}
                    />
                  )}
                  {activeSection === "werkpatroon" && (
                    <>
                      <ScheduleMatchNote
                        scheduleMinutes={scheduleMinutes}
                        contractMinutes={contractMinutes}
                      />
                      {!employerManaged && (
                        <WorkScheduleSettings scopedEmploymentId={activeEmployment.id} />
                      )}
                    </>
                  )}
                  {activeSection === "verlof" && (
                    <>
                      <LeavePoolSection
                        enabled={activeEmployment.combine_atv_with_leave}
                        employerName={activeEmployment.name}
                        canEdit={canEditActiveEmployment}
                        saving={savingPool}
                        onChange={(checked) => void saveLeavePool(checked)}
                      />
                      <LeaveRecalculationPrompt
                        query={policyQuery}
                        reloadToken={recalculationToken}
                        canManage={canEditActiveEmployment}
                      />
                      <PolicySaldoSection
                        policies={activePolicies}
                        canEdit={canEditActiveEmployment}
                        onAdd={newPolicy}
                        onEdit={editPolicy}
                      />
                      {canEditActiveEmployment && draft && (
                        <PolicyForm
                          draft={draft}
                          leaveTypes={leaveTypes}
                          currentContract={currentContract}
                          saving={saving}
                          onChange={setDraft}
                          onSubmit={savePolicy}
                          onCancel={() => setDraft(null)}
                        />
                      )}
                    </>
                  )}
                  <div className="work-guided-footer">
                    <button
                      className="button subtle"
                      type="button"
                      disabled={activeStepIndex === 0}
                      onClick={() => chooseSection(steps[activeStepIndex - 1].key)}
                    >
                      Vorige stap
                    </button>
                    <button
                      className="button primary"
                      type="button"
                      disabled={activeStepIndex === steps.length - 1}
                      onClick={() => chooseSection(steps[activeStepIndex + 1].key)}
                    >
                      Volgende stap
                    </button>
                  </div>
                </>
              ) : (
                <section className="surface settings-section empty-state compact">
                  <BriefcaseBusiness size={24} />
                  <h2>Begin met je werkgever</h2>
                  <p>
                    Voeg je werkgever en eerste contractperiode toe om je verlof goed te
                    berekenen.
                  </p>
                  <button
                    className="button primary"
                    type="button"
                    onClick={() => setAddingEmployer(true)}
                  >
                    <Plus size={17} /> Werkgever toevoegen
                  </button>
                </section>
              )}
            </div>

            <aside className="surface work-guided-aside">
              <div className="settings-heading">
                <h2>Jouw werkgever</h2>
                <p>De gegevens hieronder gelden voor je gekozen werkgever.</p>
              </div>
              {overview && overview.employments.length > 1 && (
                <label>
                  <span className="sr-only">Werkgever kiezen</span>
                  <select
                    value={activeEmploymentId ?? ""}
                    onChange={(event) => {
                      setActiveEmploymentId(Number(event.target.value));
                      setAddingEmployer(false);
                      setActiveSection("contract");
                      setDraft(null);
                    }}
                  >
                    {overview.employments.map((employment) => (
                      <option key={employment.id} value={employment.id}>
                        {employment.name}
                      </option>
                    ))}
                  </select>
                </label>
              )}
              {activeEmployment ? (
                <div className="work-guided-employer-summary">
                  <span className="work-guided-employer-icon">
                    <BriefcaseBusiness size={22} />
                  </span>
                  <strong>{activeEmployment.name}</strong>
                  <small>
                    Fulltime basis: {currentContract?.fulltime_hours_per_week ??
                      activeEmployment.fulltime_hours_per_week} uur per week
                  </small>
                </div>
              ) : (
                <p className="empty-copy">Nog geen werkgever toegevoegd.</p>
              )}
              <div className="work-guided-contract-summary">
                <h3>Huidig contract</h3>
                {currentContract ? (
                  <dl>
                    <div>
                      <dt>Uren per week</dt>
                      <dd>{currentContract.contract_hours_per_week} uur</dd>
                    </div>
                    <div>
                      <dt>Arbeidsfactor</dt>
                      <dd>{currentContract.arbeidsfactor_percentage}%</dd>
                    </div>
                    <div>
                      <dt>Ingangsdatum</dt>
                      <dd>{formatDate(currentContract.start_date)}</dd>
                    </div>
                    <div>
                      <dt>Einde contract</dt>
                      <dd>
                        {currentContract.end_date
                          ? formatDate(currentContract.end_date)
                          : "Doorlopend"}
                      </dd>
                    </div>
                  </dl>
                ) : (
                  <p className="empty-copy">
                    Voeg een contract toe om je werkpatroon te controleren.
                  </p>
                )}
              </div>
              <button
                className="button subtle work-guided-employer-action"
                type="button"
                onClick={() => {
                  setAddingEmployer(true);
                  setDraft(null);
                }}
              >
                <Plus size={17} /> Werkgever toevoegen
              </button>
            </aside>
          </div>
        </div>
      ) : (
        <>
          <section className="surface settings-section">
            <div className="settings-heading">
              <h2>Werkpatroon</h2>
              <p>
                Het werkpatroon bepaalt op welke dagen je verlof afboekt. Bij een
                volledige verlofdag op een werkdag worden de geroosterde uren
                afgeschreven.
              </p>
            </div>
            {activeSchedules.length === 0 ? (
              <p className="empty-copy">Nog geen werkpatroon ingesteld.</p>
            ) : (
              <div className="version-list">
                {activeSchedules.map((schedule) => (
                  <article key={schedule.id}>
                    <span>
                      <strong>{schedule.label || "Werkpatroon"}</strong>
                      <small>{formatDate(schedule.effective_from)}</small>
                    </span>
                    <strong>
                      {formatMinutes(schedule.total_minutes_per_week)}/week
                    </strong>
                  </article>
                ))}
              </div>
            )}
            <ScheduleMatchNote
              scheduleMinutes={scheduleMinutes}
              contractMinutes={contractMinutes}
            />
            {contractMinutes == null && overview?.employments.length === 0 && (
              <p className="empty-copy">
                Vul eerst een contract in om te controleren of het werkpatroon
                overeenkomt met je contracturen.
              </p>
            )}
          </section>

          <PolicySaldoSection
            policies={activePolicies}
            canEdit={canEdit}
            onAdd={newPolicy}
            onEdit={editPolicy}
          />
          {canEdit && draft && (
            <PolicyForm
              draft={draft}
              leaveTypes={leaveTypes}
              currentContract={currentContract}
              saving={saving}
              onChange={setDraft}
              onSubmit={savePolicy}
              onCancel={() => setDraft(null)}
            />
          )}

          <EmploymentSettings />
          <WorkScheduleSettings />
        </>
      )}

      {!canEdit && (
        <p className="read-only-note surface settings-section">
          <Scale size={17} /> Alleen een owner of organisatiebeheerder kan de werk- en
          verlofgegevens wijzigen.
        </p>
      )}
    </div>
  );
}

function ScheduleMatchNote({
  scheduleMinutes,
  contractMinutes,
}: {
  scheduleMinutes: number | null;
  contractMinutes: number | null;
}) {
  if (scheduleMinutes == null || contractMinutes == null) return null;
  const diff = scheduleMinutes - contractMinutes;
  if (diff === 0) return null;
  if (diff > 0) {
    const annualHours = Math.round((diff / 60) * 52);
    return (
      <div className="info-banner">
        <AlertTriangle size={18} />
        <span>
          Je werkpatroon bevat {formatMinutes(diff)} meer dan je contract. Dit bouwt ATV
          op (ongeveer {annualHours} uur per jaar).
        </span>
      </div>
    );
  }
  return (
    <div className="warning-banner">
      <AlertTriangle size={18} />
      <span>
        Je contract is {formatMinutes(contractMinutes)}, maar je huidige werkpatroon
        bevat {formatMinutes(scheduleMinutes)}.
      </span>
    </div>
  );
}

function LeavePoolSection({
  enabled,
  employerName,
  canEdit,
  saving,
  onChange,
}: {
  enabled: boolean;
  employerName: string;
  canEdit: boolean;
  saving: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <section className="surface settings-section">
      <div className="settings-heading">
        <h2>ATV en verlof</h2>
        <p>
          Sommige werkgevers zien ATV en verlof als een gezamenlijke pot uren. Staat dit
          aan bij {employerName}, dan kies je bij het plannen niet meer zelf: opgenomen
          uren gaan eerst van je ATV-saldo af en daarna van je verlofsaldo.
        </p>
      </div>
      <label className="toggle-field policy-toggle">
        <input
          type="checkbox"
          checked={enabled}
          disabled={!canEdit || saving}
          onChange={(event) => onChange(event.target.checked)}
        />
        <span>
          <strong>ATV en verlof als een pot</strong>
          <small>Bij het plannen van verlof gebruikt de app eerst je ATV-uren.</small>
        </span>
      </label>
    </section>
  );
}

function PolicySaldoSection({
  policies,
  canEdit,
  onAdd,
  onEdit,
}: {
  policies: WorkPolicyOverview[];
  canEdit: boolean;
  onAdd: () => void;
  onEdit: (policy: WorkPolicyOverview) => void;
}) {
  return (
    <section className="surface settings-section">
      <div className="settings-heading settings-heading-with-action">
        <div>
          <h2>Verlofregelingen & saldo</h2>
          <p>
            Het systeem berekent je persoonlijke recht automatisch uit de regeling en
            het arbeidsfactor.
          </p>
        </div>
        {canEdit && (
          <button className="button primary" type="button" onClick={onAdd}>
            <Plus size={17} /> Regeling toevoegen
          </button>
        )}
      </div>
      {policies.length === 0 && (
        <div className="empty-state compact">
          <Wallet size={24} />
          <h2>Nog geen verlofregeling</h2>
          <p>Voeg een regeling toe, het bijbehorende saldo ontstaat automatisch.</p>
        </div>
      )}
      <div className="policy-saldo-list">
        {policies.map((policy) => (
          <article key={policy.id} className="policy-saldo-card">
            <div className="policy-saldo-head">
              <span>
                <strong>{policy.leave_type_name}</strong>
                <small>
                  {policy.fulltime_annual_hours} uur
                  {policy.prorated_by_contract ? " · naar rato" : ""}
                  {policy.validity_year ? ` · ${policy.validity_year}` : ""}
                </small>
              </span>
              {canEdit && (
                <button
                  className="icon-button"
                  type="button"
                  title="Regeling wijzigen"
                  aria-label={`${policy.leave_type_name} wijzigen`}
                  onClick={() => onEdit(policy)}
                >
                  <Pencil size={15} />
                </button>
              )}
            </div>
            {policy.calculation && (
              <div className="calc-breakdown">
                <div>
                  <span>Fulltime recht</span>
                  <strong>{policy.calculation.fulltime_annual_hours} uur</strong>
                </div>
                <div>
                  <span>Fulltime werkweek</span>
                  <strong>{policy.calculation.fulltime_hours_per_week} uur</strong>
                </div>
                <div>
                  <span>Jouw contract</span>
                  <strong>
                    {policy.calculation.contract_hours_per_week ?? "-"} uur
                  </strong>
                </div>
                <div>
                  <span>Arbeidsfactor</span>
                  <strong>{policy.calculation.arbeidsfactor_percentage}%</strong>
                </div>
                <div className="calc-total">
                  <span>Jouw recht</span>
                  <strong>{policy.calculation.personal_annual_hours} uur</strong>
                </div>
              </div>
            )}
            {policy.balance && (
              <div className="balance-breakdown">
                <div>
                  <span>Toegekend</span>
                  <strong>{formatMinutes(policy.balance.current_minutes)}</strong>
                </div>
                <div>
                  <span>Opgenomen</span>
                  <strong>{formatMinutes(policy.balance.approved_minutes)}</strong>
                </div>
                <div>
                  <span>Gepland</span>
                  <strong>
                    {formatMinutes(
                      policy.balance.requested_minutes + policy.balance.planned_minutes,
                    )}
                  </strong>
                </div>
                <div>
                  <span>Beschikbaar</span>
                  <strong>{formatMinutes(policy.balance.available_minutes)}</strong>
                </div>
              </div>
            )}
          </article>
        ))}
      </div>
    </section>
  );
}

function PolicyForm({
  draft,
  leaveTypes,
  currentContract,
  saving,
  onChange,
  onSubmit,
  onCancel,
}: {
  draft: PolicyDraft;
  leaveTypes: LeaveType[];
  currentContract: { contract_hours_per_week: number } | null;
  saving: boolean;
  onChange: (draft: PolicyDraft) => void;
  onSubmit: (event: FormEvent) => void;
  onCancel: () => void;
}) {
  return (
    <form className="surface settings-section" onSubmit={onSubmit}>
      <div className="settings-heading settings-heading-with-action">
        <div>
          <h2>{draft.id ? "Verlofregeling wijzigen" : "Verlofregeling toevoegen"}</h2>
          <p>
            Kies een verloftype en vul de fulltime aanspraak in. Bij naar rato wordt dit
            vermenigvuldigd met jouw arbeidsfactor.
          </p>
        </div>
        <button
          className="icon-button"
          type="button"
          title="Sluiten"
          onClick={onCancel}
        >
          <X size={17} />
        </button>
      </div>
      <label>
        <span>Verloftype</span>
        <select
          value={draft.leave_type}
          onChange={(event) =>
            onChange({ ...draft, leave_type: Number(event.target.value) })
          }
          required
        >
          {leaveTypes.map((leaveType) => (
            <option key={leaveType.id} value={leaveType.id}>
              {leaveType.name}
            </option>
          ))}
        </select>
      </label>
      <label>
        <span>Fulltime aanspraak in uren</span>
        <span className="number-suffix">
          <input
            type="number"
            min="0"
            step="0.5"
            value={draft.fulltime_annual_hours}
            onChange={(event) =>
              onChange({ ...draft, fulltime_annual_hours: event.target.value })
            }
            required
          />
          <small>uur</small>
        </span>
      </label>
      <div className="form-row">
        <label>
          <span>Opbouw vanaf</span>
          <DateField
            value={draft.start_date}
            onChange={(value) =>
              onChange({ ...draft, start_date: value })
            }
            required
          />
        </label>
        <label>
          <span>Geldigheidsjaar</span>
          <input
            type="number"
            min="2000"
            max="2100"
            value={draft.validity_year}
            onChange={(event) =>
              onChange({ ...draft, validity_year: event.target.value })
            }
          />
        </label>
        <label>
          <span>Vervaldatum</span>
          <DateField
            value={draft.expires_on}
            onChange={(value) => onChange({ ...draft, expires_on: value })}
          />
        </label>
      </div>
      <label className="toggle-field">
        <input
          type="checkbox"
          checked={draft.prorated_by_contract}
          onChange={(event) =>
            onChange({ ...draft, prorated_by_contract: event.target.checked })
          }
        />
        <span>
          <strong>Naar rato berekenen</strong>
          <small>
            Persoonlijk recht = fulltime recht &times; (contracturen / fulltime uren).
          </small>
        </span>
      </label>
      {draft.prorated_by_contract && !currentContract && (
        <p className="field-hint">
          Vul eerst een werkgever en contract in om deze regeling naar rato te kunnen
          berekenen.
        </p>
      )}
      <button className="button primary" type="submit" disabled={saving}>
        <Check size={17} /> Regeling opslaan
      </button>
    </form>
  );
}
